/**
 * Tiny localStorage helpers, so the settings that should survive an app
 * kill/restart persist the same safe way everywhere. Android is aggressive
 * about killing a backgrounded app, so anything the skipper set (chart, palette,
 * armed alarms, where they were looking) has to be written to storage, not just
 * held in memory, or it's gone the next time the app is reopened.
 *
 * Every read merges the saved value OVER the defaults, so a profile saved by an
 * older build that didn't know about a newer field still comes back complete
 * rather than with holes. All wrapped in try/catch for SSR, private mode and a
 * full quota, where localStorage can be absent or throw.
 */
export function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    // Merge objects over the fallback so new fields keep their defaults; scalars
    // and arrays are taken as-is.
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      fallback &&
      typeof fallback === 'object' &&
      !Array.isArray(fallback)
    ) {
      return { ...(fallback as object), ...(parsed as object) } as T;
    }
    return parsed as T;
  } catch {
    return fallback;
  }
}

export function saveJson(key: string, value: unknown): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}
